import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { ContentTokenDetails, CustomUserField, Language } from '@novo/platform-common/models';
import { UserSignupInfo } from '@novo/platform-common/services/api/parse-api.interfaces';
import { ParseAPIService } from '@novo/platform-common/services/api/parse-api.service';
import { AuthService } from '@novo/platform-common/services/auth';
import { isSocialLogin, LoginType, PasswordLoginCredentials, SocialLoginCredentials, SocialLoginProvider } from '@novo/platform-common/services/auth/auth.interfaces';
import { ErrorLoggerService } from '@novo/platform-common/services/error-logger';
import { LocaleService } from '@novo/platform-common/services/locale';
import { LoginProvider, LoginProviderId, SocialLoginService } from '@novo/social-login';
import { Environment } from '@novo/website/services';
import { ProvisionalDataService } from '@novo/website/services/provisional-data';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, from, NEVER, Observable, of, timer } from 'rxjs';
import { catchError, filter, finalize, map, shareReplay, switchMap, take } from 'rxjs/operators';

/**
 * Storage key indicating the user has logged in before
 */
const RETURNING_USER_KEY = 'returningUser';

@Component({
    selector: 'app-signup-page',
    templateUrl: './signup-page.component.html',
    styleUrls: ['./signup-page.component.scss']
})
export class SignupPageComponent implements OnInit {

    readonly signupForm = new FormGroup({
        email: new FormControl('', [Validators.email], [this.validateEmailOrUsernameNotTaken.bind(this)]),
        password: new FormControl('', Validators.compose([
            Validators.required,
            Validators.pattern(/^(?=.*[A-z])(?=.*[0-9])(?=.{8,})/),
        ])),
        firstName: new FormControl('', [Validators.required]),
        lastName: new FormControl('', [Validators.required]),
        meta: new FormGroup({})
    }, [this.notIncludeEmail.bind(this)]);

    readonly loginForm = new FormGroup({
        email: new FormControl('', [Validators.email]),
        password: new FormControl('', [Validators.required]),
    });

    /**
     * Indicator during API calls
     */
    sending$: BehaviorSubject<{ loginType: LoginType, provider?: SocialLoginProvider } | undefined> = new BehaviorSubject(undefined);

    /**
     * Url to return user to after signup or login
     */
    readonly return: string | null = this.route.snapshot.queryParamMap.get('return');

    /**
     * Group identifier of the selling party. Users are added to this group
     */
    readonly vendorId: string | null = this.route.snapshot.queryParamMap.get('groupId');

    /**
     * Token that signals this account signup is linked to a content access link
     */
    readonly token: string = this.route.snapshot.queryParamMap.get('token');

    /**
     * Holds custom metadata which should be added to the user when
     * signing up for a new account.
     *
     * The parameters are filtered based on the allow custom metadata fields
     * provided by the API.
     */
    readonly userCustomMetaData$: Observable<CustomUserField[]> = this.route.queryParamMap?.pipe(
        switchMap((params: ParamMap) => {
            const token = params.get('token');
            if (token == null) {
                return of([]);
            }
            return this.api.getDetailsByContentToken(token).pipe(
                map((details: ContentTokenDetails) => {
                    const customUserFields: CustomUserField[] = details.group && details.group.customUserFields;
                    if (customUserFields) {
                        const metaGroup: FormGroup = this.signupForm.controls['meta'] as FormGroup;
                        customUserFields.forEach(field => {
                            metaGroup.addControl(field.name, new FormControl(params.get(field.name)));
                        });
                    }
                    return customUserFields;
                }),
                catchError(err => {
                    return of([]);
                }),
            );
        }),
        shareReplay(1),
    );

    /**
     * Set active tab based on explitly based on mode query parameter
     * or based on whether we know this is a returning user
     */
    selectedTab$: Observable<string> = this.route.queryParamMap.pipe(
        map(params => {
            const mode = params.get('mode');
            if (mode == null) {
                return this.cookie.check(RETURNING_USER_KEY) ? 'login' : 'sign-up';
            } else {
                return mode;
            }
        }));

    /**
     * Holds errors returned by API
     */
    error: Error;

    readonly loginProviders: Map<LoginProviderId, LoginProvider> = this.socialLoginService.providers;

    constructor(
        private route: ActivatedRoute,
        private auth: AuthService,
        private socialLoginService: SocialLoginService,
        public api: ParseAPIService,
        private errorLogger: ErrorLoggerService,
        public router: Router,
        private provisionalData: ProvisionalDataService,
        private locale: LocaleService,
        private environment: Environment,
        private cookie: CookieService
    ) {
    }

    ngOnInit() {
        this.signupForm.valueChanges.subscribe((values) => {
            // Set email address in login form in case user is already signed up and wants to login
            this.loginForm.controls.email.setValue(values.email);
        });

        const provisionalUser = this.provisionalData.getProvisionalUser();
        if (provisionalUser) {
            this.signupForm.controls.firstName.setValue(provisionalUser.firstName);
            this.signupForm.controls.lastName.setValue(provisionalUser.lastName);
        }

        this.userCustomMetaData$.subscribe();
    }

    selectTab(tabName: string) {
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { 'mode': tabName },
            queryParamsHandling: 'merge'
        });
    }

    /**
     * Validator function which validates whether the given control's value
     * is a unique username or email
     *
     * @param control
     */
    validateEmailOrUsernameNotTaken(control: AbstractControl): Observable<ValidationErrors | null> {
        return timer(500).pipe(
            map(_ => control.value),
            filter(v => v && v.length >= 5),
            switchMap(v => this.api.isUniqueUsernameOrEmail(v, v)),
            map(unique => (!unique ? { taken: true } : null))
        );
    }

    private removeError(control: AbstractControl, error: string) {
        const err = control.errors; // get control errors
        if (err) {
            delete err[error]; // delete your own error
            if (!Object.keys(err).length) { // if no errors left
                control.setErrors(null); // set control errors to null making it VALID
            } else {
                control.setErrors(err); // controls got other errors so set them back
            }
        }
    }

    notIncludeEmail(ac: AbstractControl): ValidationErrors | null {
        const passCtrl = ac.get('password');
        const emailCtrl = ac.get('email');

        if (!passCtrl || !emailCtrl) {
            return null;
        }

        const password = passCtrl.value;
        const email = emailCtrl.value;
        if (email.length > 0 && password.indexOf(email) >= 0) {
            passCtrl.setErrors({ includesEmail: true });
        } else {
            this.removeError(passCtrl, 'includesEmail');
        }
        return null;
    }

    signupWithIdProvider(provider: LoginProviderId) {
        from(this.socialLoginService.signIn(provider)).pipe(
            catchError(err => {
                this.errorLogger.captureException(err, { extra: { provider } });
                return NEVER;
            }),
            filter(result => result != null),
            map(result => {
                const email = result.email != null ? result.email : this.signupForm.controls.email.value;
                const firstName = result.firstName != null ? result.firstName : this.signupForm.controls.firstName.value;
                const lastName = result.lastName != null ? result.lastName : this.signupForm.controls.lastName.value;
                const meta = this.signupForm.controls.meta.value;
                const { idToken, accessToken, id: userId } = result;

                const credentials = { provider, idToken, accessToken, userId };
                const userInfo = { firstName, lastName, email, meta };

                this._signup('social', credentials, userInfo);
            })
        ).subscribe();
    }

    signupWithPassword() {
        if (!this.signupForm.valid) {
            return;
        }
        const { email, password, firstName, lastName, meta } = this.signupForm.value;
        const credentials = { username: email, password };
        const userInfo = { firstName, lastName, email, meta };

        this._signup('password', credentials, userInfo);
    }

    _signup(loginType: LoginType, loginCredentials: SocialLoginCredentials | PasswordLoginCredentials, userInfo: UserSignupInfo) {

        // Don't sign up twice
        if (this.sending$.value !== undefined) {
            return;
        }

        let provider: LoginProviderId | undefined;
        if (isSocialLogin(loginType, loginCredentials)) {
            provider = loginCredentials.provider;
        }

        this.sending$.next({ loginType, provider });
        this.error = null;

        const approvedDocumentUrls = [
            this.environment.get('termsOfServiceUrl'),
            this.environment.get('privacyPolicyUrl')
        ];

        this.locale.language$.pipe(
            take(1),
            switchMap((lang: Language) => {
                userInfo.language = lang.code;
                userInfo.organisation = this.vendorId;

                if (this.token) {
                    return this.api.signupByToken(this.token, loginType, loginCredentials, userInfo, approvedDocumentUrls);
                }
                return this.api.signup(loginType, loginCredentials, userInfo, approvedDocumentUrls);
            }),
            switchMap(_ => {
                return this.auth.login(loginType, loginCredentials);
            }),
            finalize(() => {
                this.sending$.next(undefined);
            }),
            catchError(err => {
                this.errorLogger.captureException(err);
                console.error(err);
                this.error = err;
                return NEVER;
            })
        ).subscribe(_ => {
            if (this.return) {
                this.router.navigateByUrl(this.return);
            }
        });
    }

    loginWithIdProvider(provider: LoginProviderId) {
        from(this.socialLoginService.signIn(provider)).pipe(
            catchError(err => {
                this.errorLogger.captureException(err, { extra: { provider } });
                return NEVER;
            }),
            filter(result => result != null),
            map(result => this._login('social', {
                provider,
                userId: result.id,
                idToken: result.idToken,
                accessToken: result.accessToken,
                email: result.email
            }))
        ).subscribe();
    }

    loginWithPassword() {
        if (!this.loginForm.valid) {
            return;
        }
        const { email, password } = this.loginForm.value;
        this._login('password', { username: email, password });
    }

    _login(type: LoginType, credentials: PasswordLoginCredentials | SocialLoginCredentials) {

        let provider: LoginProviderId | undefined;
        if (isSocialLogin(type, credentials)) {
            provider = credentials.provider;
        }

        this.sending$.next({ loginType: type, provider });
        this.error = null;

        this.auth.login(type, credentials).pipe(
            catchError(err => {
                this.error = err;
                this.sending$.next(undefined);
                return NEVER;
            }),
        ).subscribe(_ => {
            this.sending$.next(undefined);
            this.cookie.set(RETURNING_USER_KEY, '1', 365);
            if (this.return) {
                this.router.navigateByUrl(this.return);
            }
        });
    }

}
